import { HierarchicalNode } from './TSAdvancedHierarchicalTable';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface ColumnState {
  isOpen: boolean;
  isVisible: boolean;
  isHorizontal: boolean;
}

export interface ColumnOrientationState {
  columnStates: Record<string, ColumnState>;
}

// Action Types
export const ACTION_TYPES = {
  INITIALIZE_COLUMNS: 'INITIALIZE_COLUMNS',
  COLUMN_CLICK: 'COLUMN_CLICK',
  RESET_ALL_COLUMNS: 'RESET_ALL_COLUMNS',
} as const;

export type ActionType = typeof ACTION_TYPES[keyof typeof ACTION_TYPES];

// Action Interfaces
export interface InitializeColumnsAction {
  type: typeof ACTION_TYPES.INITIALIZE_COLUMNS;
  payload: {
    columnContext: Record<string, HierarchicalNode>;
    initialOpenColumns?: string[];
  };
}

export interface ColumnClickAction {
  type: typeof ACTION_TYPES.COLUMN_CLICK;
  payload: {
    path: string[];
    columnContext: Record<string, HierarchicalNode>;
  };
}

export interface ResetAllColumnsAction {
  type: typeof ACTION_TYPES.RESET_ALL_COLUMNS;
}

export type ColumnOrientationAction =
  | InitializeColumnsAction
  | ColumnClickAction
  | ResetAllColumnsAction;

// ============================================================================
// INITIAL STATE
// ============================================================================

export const initialState: ColumnOrientationState = {
  columnStates: {},
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Retrieves a hierarchical node from a given path
 * @param path - Array of node IDs representing the path
 * @param nodes - Record of nodes to search in
 * @returns The node at the specified path or undefined if not found
 */
export const getNodeFromPath = (
  path: string[],
  nodes: Record<string, HierarchicalNode>,
): HierarchicalNode | undefined => {
  if (path.length === 0) {
    return undefined;
  }
  if (path.length === 1) {
    return nodes[path[0]];
  }

  const [first, ...rest] = path;
  const firstNode = nodes[first];
  if (!firstNode) {
    return undefined;
  }

  // Convert subNodes array to a record for recursive lookup
  const subNodesRecord: Record<string, HierarchicalNode> = {};
  firstNode.subNodes.forEach((subNode) => {
    subNodesRecord[subNode.id] = subNode;
  });

  return getNodeFromPath(rest, subNodesRecord);
};

/**
 * Gets all descendant nodes along a given path
 * @param path - Array of node IDs representing the path
 * @param columnContext - Record of all available nodes
 * @returns Array of descendant nodes in order from parent to child
 */
export const getDescendantsNodes = (
  path: string[],
  columnContext: Record<string, HierarchicalNode>,
): HierarchicalNode[] => {
  const descendants: HierarchicalNode[] = [];

  for (let i = 0; i < path.length; i += 1) {
    const currentPath = path.slice(0, i + 1);
    const descendantNode = getNodeFromPath(currentPath, columnContext);
    if (descendantNode) {
      descendants.push(descendantNode);
    }
  }

  return descendants;
};

/**
 * Determines if a column should be displayed horizontally
 * @param pathKey - The path key of the column (e.g., "Mathematics.Statistics")
 * @param columnStates - Current state of all columns
 * @param columnContext - Available nodes context
 * @returns true if the column should be horizontal, false otherwise
 */
const calculateIsHorizontal = (
  pathKey: string,
  columnStates: Record<string, ColumnState>,
  columnContext: Record<string, HierarchicalNode>,
): boolean => {
  const columnState = columnStates[pathKey];
  if (!columnState) {
    return false;
  }

  const path = pathKey.split('.');
  const node = getNodeFromPath(path, columnContext);
  const hasChildren = node && node.subNodes.length > 0;

  // Leaf nodes are always horizontal when visible
  if (!hasChildren) {
    return true;
  }

  // Closed group nodes are not horizontal
  if (!columnState.isOpen) {
    return false;
  }

  // For open group nodes, check if any descendants are open
  const hasOpenDescendants = node.subNodes.some((child) => {
    const childPathKey = [...path, child.id].join('.');
    const childState = columnStates[childPathKey];
    return childState && childState.isOpen;
  });

  // If it's open but has no open descendants, it should be horizontal
  return !hasOpenDescendants;
};

// ============================================================================
// STATE MANAGEMENT FUNCTIONS
// ============================================================================

/**
 * Creates initial column states for all nodes in the hierarchy
 * @param columnContext - Record of all available nodes
 * @param initialOpenColumns - Optional array of column IDs to open initially
 * @returns Record of column states for all nodes
 */
const createInitialColumnStates = (
  columnContext: Record<string, HierarchicalNode>,
  initialOpenColumns?: string[],
): Record<string, ColumnState> => {
  const states: Record<string, ColumnState> = {};

  const processNode = (node: HierarchicalNode, path: string[]): void => {
    const currentPath = [...path, node.id];
    const currentPathKey = currentPath.join('.');

    // Check if this column should be initially open
    const shouldBeOpen = initialOpenColumns?.includes(currentPathKey) ?? false;

    // Create state for all nodes, including leaf nodes
    states[currentPathKey] = {
      isOpen: shouldBeOpen,
      isVisible: true,
      isHorizontal: false,
    };

    // Process all subnodes recursively
    node.subNodes.forEach((subNode) => {
      processNode(subNode, currentPath);
    });
  };

  // Process each root node
  Object.values(columnContext).forEach((node) => {
    processNode(node, []);
  });

  // Handle sibling visibility for initially open columns
  if (initialOpenColumns && initialOpenColumns.length > 0) {
    initialOpenColumns.forEach((columnId) => {
      const path = columnId.split('.');
      if (path.length > 1) {
        // Only manage sibling visibility for non-root columns
        manageSiblingVisibility(path, columnContext, states);
      }
    });
  }

  return states;
};

/**
 * Closes all descendants of a given node
 * @param path - Path to the parent node
 * @param node - The parent node
 * @param newStates - Mutable reference to the states being updated
 */
const closeAllDescendants = (
  path: string[],
  node: HierarchicalNode,
  newStates: Record<string, ColumnState>,
): void => {
  const closeDescendants = (
    currentPath: string[],
    currentNode: HierarchicalNode,
  ): void => {
    currentNode.subNodes.forEach((child) => {
      const childPath = [...currentPath, child.id];
      const childKey = childPath.join('.');

      if (newStates[childKey]) {
        // eslint-disable-next-line no-param-reassign
        newStates[childKey] = {
          ...newStates[childKey],
          isOpen: false,
          isVisible: true,
        };
        // Recursively close all descendants
        closeDescendants(childPath, child);
      }
    });
  };

  closeDescendants(path, node);
};

/**
 * Manages sibling visibility when a column is opened
 * @param path - Path to the opened column
 * @param columnContext - Available nodes context
 * @param newStates - Mutable reference to the states being updated
 */
const manageSiblingVisibility = (
  path: string[],
  columnContext: Record<string, HierarchicalNode>,
  newStates: Record<string, ColumnState>,
): void => {
  const parentPath = path.slice(0, -1);
  if (parentPath.length === 0) {
    return; // Root level has no siblings
  }

  const parentNode = getNodeFromPath(parentPath, columnContext);
  if (!parentNode) {
    return;
  }

  const openedChildId = path[path.length - 1];

  // Hide all siblings of the opened column
  parentNode.subNodes.forEach((sibling) => {
    if (String(sibling.id) !== String(openedChildId)) {
      const siblingPath = [...parentPath, sibling.id];
      const siblingKey = siblingPath.join('.');
      if (newStates[siblingKey]) {
        // eslint-disable-next-line no-param-reassign
        newStates[siblingKey] = {
          ...newStates[siblingKey],
          isVisible: false,
        };
      }
    }
  });
};

/**
 * Shows all siblings when a column becomes horizontal
 * @param path - Path to the column that became horizontal
 * @param columnContext - Available nodes context
 * @param newStates - Mutable reference to the states being updated
 */
const showAllSiblings = (
  path: string[],
  columnContext: Record<string, HierarchicalNode>,
  newStates: Record<string, ColumnState>,
): void => {
  const parentPath = path.slice(0, -1);
  if (parentPath.length === 0) {
    return; // Root level has no siblings
  }

  const parentNode = getNodeFromPath(parentPath, columnContext);
  if (!parentNode) {
    return;
  }

  // Show all siblings
  parentNode.subNodes.forEach((sibling) => {
    const siblingPath = [...parentPath, sibling.id];
    const siblingKey = siblingPath.join('.');
    if (newStates[siblingKey]) {
      // eslint-disable-next-line no-param-reassign
      newStates[siblingKey] = {
        ...newStates[siblingKey],
        isVisible: true,
      };
    }
  });
};

/**
 * Recalculates isHorizontal for all column states
 * @param newStates - Mutable reference to the states being updated
 * @param columnContext - Available nodes context
 */
const recalculateHorizontalStates = (
  newStates: Record<string, ColumnState>,
  columnContext: Record<string, HierarchicalNode>,
): void => {
  Object.keys(newStates).forEach((stateKey) => {
    // eslint-disable-next-line no-param-reassign
    newStates[stateKey].isHorizontal = calculateIsHorizontal(
      stateKey,
      newStates,
      columnContext,
    );
  });
};

// ============================================================================
// REDUCER
// ============================================================================

/**
 * Main reducer for column orientation state management
 * Handles navigation-based column interactions with hierarchical data
 */
export const groupColumnReducer = (
  state: ColumnOrientationState,
  action: ColumnOrientationAction,
): ColumnOrientationState => {
  switch (action.type) {
    case ACTION_TYPES.INITIALIZE_COLUMNS: {
      const { columnContext, initialOpenColumns } = action.payload;
      const newStates = createInitialColumnStates(columnContext, initialOpenColumns);
      recalculateHorizontalStates(newStates, columnContext);

      return {
        ...state,
        columnStates: newStates,
      };
    }

    case ACTION_TYPES.COLUMN_CLICK: {
      const { path, columnContext: columnContextData } = action.payload;
      const pathKey = path.join('.');
      const newStates = { ...state.columnStates };

      if (!newStates[pathKey]) {
        return state; // Column doesn't exist
      }

      const currentState = newStates[pathKey];
      const isFirstLevel = path.length === 1;
      const isCurrentlyHorizontal = calculateIsHorizontal(
        pathKey,
        newStates,
        columnContextData || {},
      );

      // Navigation behavior logic
      if (currentState.isOpen && !isCurrentlyHorizontal) {
        // Case 1: Open but not horizontal (has open descendants) - close all descendants
        const node = getNodeFromPath(path, columnContextData || {});
        if (node) {
          closeAllDescendants(path, node, newStates);
        }
      } else if (currentState.isOpen && isCurrentlyHorizontal && isFirstLevel) {
        // Case 2: Open and horizontal first-level node - close itself
        // eslint-disable-next-line no-param-reassign
        newStates[pathKey] = {
          ...newStates[pathKey],
          isOpen: false,
        };
      } else if (!currentState.isOpen) {
        // Case 3: Closed node - open it (navigation behavior)
        // eslint-disable-next-line no-param-reassign
        newStates[pathKey] = {
          ...newStates[pathKey],
          isOpen: true,
          isVisible: true, // Ensure opened column is visible
        };
      }
      // Case 4: Open and horizontal non-first-level node - no action (navigation behavior)

      // Handle sibling visibility
      if (newStates[pathKey]?.isOpen && columnContextData) {
        manageSiblingVisibility(path, columnContextData, newStates);
      } else if (newStates[pathKey]?.isHorizontal && columnContextData) {
        showAllSiblings(path, columnContextData, newStates);
      }

      // Recalculate horizontal states for all columns
      recalculateHorizontalStates(newStates, columnContextData || {});

      return {
        ...state,
        columnStates: newStates,
      };
    }

    case ACTION_TYPES.RESET_ALL_COLUMNS: {
      const newStates = { ...state.columnStates };
      Object.keys(newStates).forEach((key) => {
        newStates[key] = {
          ...newStates[key],
          isOpen: false,
          isVisible: true,
          isHorizontal: false,
        };
      });

      return {
        ...state,
        columnStates: newStates,
      };
    }

    default:
      return state;
  }
};
